import React, { useState, useEffect } from 'react';
import axios from 'axios';
import showToast from '../../utils/toastHelper';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [reconciling, setReconciling] = useState(false);
  const [allStats, setAllStats] = useState({ 
    total: 0, 
    pending: 0, 
    successful: 0, 
    failed: 0, 
    totalRevenue: 0, 
    platformEarnings: 0,
    voteServiceCharges: 0,
    packageRevenue: 0
  });

  useEffect(() => {
    fetchTransactions();
    fetchAllStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, pagination.page, pagination.limit]);

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem('token');
      const statusParam = filter !== 'all' ? `&status=${filter}` : '';
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/payments/transactions?page=${pagination.page}&limit=${pagination.limit}${statusParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTransactions(response.data.data?.transactions || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.data?.pagination?.total || 0,
        totalPages: response.data.data?.pagination?.totalPages || 0,
      }));
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      showToast('Failed to load transactions', 'error');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const [transactionsRes, statsRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_API_URL}/payments/transactions?page=1&limit=1000`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${process.env.REACT_APP_API_URL}/admin/dashboard/stats`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
      ]);
      
      const allTransactions = transactionsRes.data.data?.transactions || [];
      const successfulTransactions = allTransactions.filter(t => t.status === 'successful');
      const totalRevenue = successfulTransactions.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
      
      // Use backend-calculated platform earnings for consistency
      const dashboardStats = statsRes.data.data || {};
      
      setAllStats({
        total: allTransactions.length,
        pending: allTransactions.filter(t => t.status === 'pending').length,
        successful: successfulTransactions.length,
        failed: allTransactions.filter(t => t.status === 'failed').length,
        totalRevenue: totalRevenue,
        platformEarnings: dashboardStats.platformEarnings || 0,
        voteServiceCharges: dashboardStats.voteServiceCharges || 0,
        packageRevenue: dashboardStats.packageRevenue || 0,
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleReconcile = async () => {
    if (!window.confirm('This will reconcile all pending transactions. Continue?')) {
      return;
    }
    
    setReconciling(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/transactions/reconcile`,
        { checkAll: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showToast(`Reconciliation complete! Processed: ${response.data.data.processed}, Successful: ${response.data.data.successful}, Failed: ${response.data.data.failed}`, 'success');
      fetchTransactions();
      fetchAllStats();
    } catch (error) {
      console.error('Reconciliation failed:', error);
      showToast(error.response?.data?.error || 'Failed to reconcile transactions', 'error');
    } finally {
      setReconciling(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'warning',
      successful: 'success',
      completed: 'success',
      failed: 'danger',
      refunded: 'info',
      cancelled: 'secondary',
    };
    return `badge bg-${badges[status] || 'secondary'}`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'UGX',
    }).format(amount);
  };

  const getTransactionType = (transaction) => {
    if (transaction.paymentType === 'subscription' || transaction.flutterwaveTxRef?.startsWith('SUB_')) {
      return 'subscription';
    }
    return transaction.paymentType || 'vote';
  };

  const getTransactionTypeLabel = (transaction) => {
    const type = getTransactionType(transaction);
    if (type === 'package') return 'Package';
    if (type === 'subscription') return 'Subscription';
    return 'Vote';
  };

  const getTransactionTypeBadge = (transaction) => {
    const type = getTransactionType(transaction);
    if (type === 'vote') return 'bg-primary';
    if (type === 'subscription') return 'bg-success';
    return 'bg-info';
  };

  const getOrganizationName = (transaction) => (
    transaction.position?.election?.organization?.name
    || transaction.election?.organization?.name
    || transaction.user?.organization?.name
    || '-'
  );

  const getContextName = (transaction) => {
    const type = getTransactionType(transaction);
    if (type === 'subscription') return 'Annual Subscription';
    return transaction.position?.election?.name || transaction.election?.name || '-';
  };

  const getPlatformEarnings = (transaction) => {
    if (transaction.status !== 'successful') return '-';
    const type = getTransactionType(transaction);
    if (type === 'package' || type === 'subscription') {
      return formatAmount(transaction.amount, transaction.currency);
    }
    return formatAmount(300, 'UGX');
  };

  const filteredTransactions = transactions
    .filter(t => 
      searchTerm === '' || 
      t.user?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.flutterwaveTxRef?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.flutterwaveTxId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.position?.election?.organization?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.election?.organization?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.user?.organization?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="mb-4">
        <h3 className="mb-1" style={{ fontWeight: '600' }}>Platform Transactions</h3>
        <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
          Track all payments and revenue across the platform
        </p>
      </div>

      {/* Stats Cards */}
      {/* <div className="row mb-4">
        <div className="col-lg-3 col-md-6 mb-3">
          <div className="card h-100" style={{ border: 'none', borderLeft: '4px solid #3b82f6' }}>
            <div className="card-body">
              <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>
                Total Processed
              </div>
              <div className="h4 mb-0 mt-2" style={{ fontWeight: '700', color: '#3b82f6' }}>
                {formatAmount(allStats.totalRevenue, 'UGX')}
              </div>
              <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                Organization revenue
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6 mb-3">
          <div className="card h-100" style={{ border: 'none', borderLeft: '4px solid #10b981' }}>
            <div className="card-body">
              <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>
                Platform Earnings
              </div>
              <div className="h4 mb-0 mt-2" style={{ fontWeight: '700', color: '#10b981' }}>
                {formatAmount(allStats.platformEarnings, 'UGX')}
              </div>
              <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                Service: {formatAmount(allStats.voteServiceCharges, 'UGX')} | Packages: {formatAmount(allStats.packageRevenue, 'UGX')}
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-4 mb-3">
          <div className="card h-100" style={{ border: 'none', borderLeft: '4px solid #667eea' }}>
            <div className="card-body">
              <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>
                Transactions
              </div>
              <div className="h4 mb-0 mt-2" style={{ fontWeight: '700' }}>
                {allStats.total}
              </div>
              <div className="text-muted mt-1" style={{ fontSize: '0.75rem', visibility: 'hidden' }}>
                &nbsp;
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-4 mb-3">
          <div className="card h-100" style={{ border: 'none', borderLeft: '4px solid #f59e0b' }}>
            <div className="card-body">
              <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>
                Pending
              </div>
              <div className="h4 mb-0 mt-2" style={{ fontWeight: '700' }}>
                {allStats.pending}
              </div>
              <div className="text-muted mt-1" style={{ fontSize: '0.75rem', visibility: 'hidden' }}>
                &nbsp;
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-4 mb-3">
          <div className="card h-100" style={{ border: 'none', borderLeft: '4px solid #ef4444' }}>
            <div className="card-body">
              <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>
                Failed
              </div>
              <div className="h4 mb-0 mt-2" style={{ fontWeight: '700' }}>
                {allStats.failed}
              </div>
              <div className="text-muted mt-1" style={{ fontSize: '0.75rem', visibility: 'hidden' }}>
                &nbsp;
              </div>
            </div>
          </div>
        </div>
      </div> */}

      {/* Filters and Search */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3 mb-md-0">
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('all')}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${filter === 'successful' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('successful')}
                >
                  Successful
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${filter === 'pending' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('pending')}
                >
                  Pending
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${filter === 'failed' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('failed')}
                >
                  Failed
                </button>
              </div>
            </div>
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search by user, organization or reference..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-2 text-end">
              <button
                className="btn btn-warning btn-sm w-100"
                onClick={handleReconcile}
                disabled={reconciling || allStats.pending === 0}
              >
                {reconciling ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Reconciling...
                  </>
                ) : (
                  <>
                    <i className="bi bi-arrow-repeat me-2"></i>
                    Reconcile ({allStats.pending})
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="card">
        <div className="card-body">
          {filteredTransactions.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-receipt" style={{ fontSize: '3rem', color: '#d1d5db' }}></i>
              <p className="mt-3 text-muted">No transactions found</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>User</th>
                    <th>Organization</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Earnings</th>
                    <th>Status</th>
                    <th>Reason</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((transaction) => (
                    <tr key={transaction.id}>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        <div>{transaction.flutterwaveTxRef || '-'}</div>
                        {transaction.flutterwaveTxId && (
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            TX: {transaction.flutterwaveTxId}
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                          {transaction.user?.fullName || 'Unknown'}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {transaction.user?.email}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                          {getOrganizationName(transaction)}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {getContextName(transaction)}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${getTransactionTypeBadge(transaction)}`} style={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>
                          {getTransactionTypeLabel(transaction)}
                        </span>
                      </td>
                      <td style={{ fontWeight: '600' }}>
                        {formatAmount(transaction.amount, transaction.currency)}
                      </td>
                      <td style={{ fontWeight: '600', color: transaction.status === 'successful' ? '#10b981' : '#9ca3af' }}>
                        {getPlatformEarnings(transaction)}
                      </td>
                      <td>
                        <span className={getStatusBadge(transaction.status)} style={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>
                          {transaction.status}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.75rem', color: '#6b7280', maxWidth: '200px' }}>
                        {transaction.reason || '-'}
                      </td>
                      <td style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        {formatDate(transaction.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mt-4">
          <div className="d-flex align-items-center gap-3">
            <div className="text-muted" style={{ fontSize: '0.875rem' }}>
              Showing {Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total)} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} transactions
            </div>
            <div className="d-flex align-items-center gap-2">
              <label htmlFor="pageSize" className="text-muted mb-0" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Per page:</label>
              <select
                id="pageSize"
                className="form-select form-select-sm"
                value={pagination.limit}
                onChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                style={{ width: 'auto' }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          {pagination.totalPages > 1 && (
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  Previous
                </button>
              </li>
              {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                const pageNum = pagination.page <= 3 ? i + 1 : pagination.page - 2 + i;
                if (pageNum > pagination.totalPages) return null;
                return (
                  <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                    >
                      {pageNum}
                    </button>
                  </li>
                );
              })}
              <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
          )}
        </div>
      )}
    </div>
  );
};

export default Transactions;
